import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { FormFieldComponent } from '../form-field/form-field.component';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { IIcon } from '../../../icon/icon.component';
import { IFormFieldErrorMessages } from '../../form-master.interface';
import { ISelectOption } from './select-field.interface';
import { List } from 'immutable';
import {
  ITooltip,
  TooltipType,
} from '../../../../services/tooltip-sequence/i2g-tooltip-sequence.data';
import { I2gTooltipSequenceService } from '../../../../services/tooltip-sequence/i2g-tooltip-sequence.service';

@Component({
  selector: 'i2g-select-field',
  templateUrl: 'select-field.component.html',
  styleUrls: ['select-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectFieldComponent
  extends FormFieldComponent
  implements OnInit, OnDestroy {
  @Input() options: List<ISelectOption>;
  @Input() alignment: FormAlignmentsEnum;
  @Input() label: string;
  @Input() labelIcon: IIcon;
  @Input() labelTooltip: ITooltip;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() description: string;
  @Input() placeholder: string;
  @Input() fieldControl: AbstractControl;
  @Input() leftIcon: IIcon;
  @Input() disabled: boolean;
  // blocker is a callback that gets called before the field is set and can cancel
  // changing the field's value depending on what the returned promise resolves to
  @Input() blocker: (selectOption: ISelectOption) => Promise<ISelectOption>;
  @Input() focusableTrigger$: Observable<void>;
  @Input() maxlength = -1;
  @Input() e2eId: string;
  @Input() layoutDirection: 'row' | 'column' = 'row';
  @Input() valueAlignment: 'left' | 'center' | 'right' = 'left';
  @Input() errorMessages: IFormFieldErrorMessages;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() trackingSelected: ITrackingAction;
  @Input() trackingScreenName: string;
  @Input() trackingAdditionalProperties: any;

  @Output() inputFocus: EventEmitter<string> = new EventEmitter<string>();
  @Output() inputBlur: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  inputKeyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output()
  selectOption: EventEmitter<ISelectOption> = new EventEmitter<ISelectOption>();

  @ViewChildren('option', { read: ElementRef })
  optionRefs: QueryList<ElementRef>;
  @ViewChild('input', { read: ElementRef, static: true }) inputRef;

  private _fieldSub: Subscription;
  private _emptyFieldErrorAction: ITrackingAction;
  private _lastMouseX: number;
  private _lastMouseY: number;

  isOpen = false;
  filteredOptions: ISelectOption[];
  selectionIndex = 0;
  selectFieldControl: FormControl;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    private _tooltipService: I2gTooltipSequenceService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingService: I2gNgTrackingService
  ) {
    super(changeDetectorRef, translateService);
  }

  ngOnInit() {
    const initialOption = this._getOptionFromValue(this.fieldControl.value);
    const isFieldEmpty = initialOption === null || initialOption === void 0;
    const initialKey = isFieldEmpty ? void 0 : initialOption.key;

    this.selectFieldControl = new FormControl(initialKey);
    this.filteredOptions = this.options.toJS();

    this._fieldSub = this.fieldControl.valueChanges.subscribe(() => {
      const option = this._getOptionFromValue(this.fieldControl.value);
      this.selectFieldControl.setValue(option && option.key);
    });

    this._emptyFieldErrorAction = this._trackingActionBuilder.buildErrorInvoked(
      'field_not_populated',
      this.fieldId,
      this.trackingScreenName
    );
  }

  private _getOptionFromKey(key: string) {
    return this.options.find((option) => option.key === key);
  }

  private _getOptionFromValue(value: string) {
    return this.options.find((option) => option.value === value);
  }

  private _getOptionIndexFromValue(value: string) {
    return this.options.findIndex((option) => option.value === value);
  }

  private _validateInput() {
    this.fieldControl.updateValueAndValidity({ emitEvent: false });
    const errors = this.fieldControl.errors;
    if (errors && errors.required) {
      this._trackingService.trackEvent(
        this.trackingObject,
        this._emptyFieldErrorAction
      );
    }
  }

  onInputChange(newContent: string) {
    this.selectionIndex = 0;
    this._updateFilteredOptions(newContent);
    this.fieldControl.markAsDirty();
    this._open();
  }

  private _updateFilteredOptions(newSelectInputValue: string) {
    this.filteredOptions = this.options
      .toJS()
      .filter((option) =>
        this._normalizeString(option.key).startsWith(
          this._normalizeString(newSelectInputValue)
        )
      );
  }

  // TODO: replace with Fuse for superior UX
  // adapted from: https://stackoverflow.com/a/37511463/2455153
  private _normalizeString(input: string) {
    return input
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private _open() {
    if (!this.isOpen) {
      this.isOpen = true;
      this.filteredOptions = this.options.toJS();
      this.selectionIndex = this._getOptionIndexFromValue(
        this.fieldControl.value
      );
      // wait for options list to render before scrolling it to the selected element
      setTimeout(() => this._scrollToSelection());
    }
  }

  pickItem(index: number) {
    if (this.filteredOptions.length === 0) {
      return;
    }
    this.selectionIndex = index;
    const selection = this.filteredOptions[index];
    this._setSelectedOption(selection);
    this.fieldControl.markAsDirty();
    this._updateFilteredOptions(this.selectFieldControl.value);
    this.isOpen = false;

    return false; // prevent input element from losing focus when an option is clicked
  }

  private _selectOption(option: ISelectOption) {
    this._trackOnSelect(option);
    this.selectOption.emit(option);
    this.selectFieldControl.setValue(option.key);
    this.fieldControl.setValue(option.value);
  }

  private _setSelectedOption(selectedOption: ISelectOption) {
    if (selectedOption.value !== this.fieldControl.value) {
      if (
        this.blocker &&
        (selectedOption.block === undefined || selectedOption.block)
      ) {
        this.blocker(selectedOption).then((result: ISelectOption) => {
          if (result) {
            this._selectOption(result);
          }
        });
      } else {
        this._selectOption(selectedOption);
      }
    }
  }

  hoverItem(event: MouseEvent, index: number) {
    // hack: don't hover if mouse event firing as a result of scrollIntoView after pressing UP/DOWN
    if (
      !(
        this._lastMouseX - event.clientX === 0 &&
        this._lastMouseY - event.clientY === 0
      )
    ) {
      this.selectionIndex = index;
    }
  }

  onMouseMove(event: MouseEvent) {
    this._lastMouseX = event.clientX;
    this._lastMouseY = event.clientY;
  }

  onFocus() {
    this.inputFocus.emit(this.fieldControl.value);

    this._open();

    this.inputRef.nativeElement.select();
  }

  onBlur() {
    this.inputBlur.emit(this.fieldControl.value);

    this._validateInput();

    this.isOpen = false;

    const selectedOption = this._getOptionFromKey(
      this.selectFieldControl.value
    );
    if (selectedOption) {
      this._setSelectedOption(selectedOption);
    } else {
      const option = this._getOptionFromValue(this.fieldControl.value);
      this.selectFieldControl.setValue(option && option.key);
    }
  }

  onClick() {
    this._open();
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      this.isOpen = false;
    }

    if (event.key === 'Enter') {
      this.pickItem(this.selectionIndex);
    }
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      this._open();
      const deltaIndex = event.key === 'ArrowDown' ? +1 : -1;
      this.selectionIndex =
        (this.filteredOptions.length + this.selectionIndex + deltaIndex) %
        this.filteredOptions.length;
      this._scrollToSelection();
    }
  }

  showInfoTooltip(e: Event) {
    e.preventDefault();

    this._tooltipService.start({
      isDismissible: true,
      showProgress: false,
      tooltips: [this.labelTooltip],
      tooltipType: TooltipType.default,
    });
  }

  private _scrollToSelection() {
    const selectionRef = this.optionRefs.toArray()[this.selectionIndex];
    if (selectionRef) {
      selectionRef.nativeElement.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  private _trackOnSelect(optionValue: ISelectOption) {
    if (
      this.trackingObject &&
      this.trackingSelected &&
      optionValue.trackingValue
    ) {
      this._trackingService.trackEvent(
        this.trackingObject,
        this.trackingSelected.setValue(optionValue.trackingValue),
        this.trackingAdditionalProperties
      );
    }
  }

  ngOnDestroy() {
    if (this._fieldSub) {
      this._fieldSub.unsubscribe();
    }

    super.ngOnDestroy();
  }
}
